import {
  useEffect,
  useId,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileUp, X } from "lucide-react";
import { CredentialCategory } from "@api/shared-runtime/enums";
import { toast } from "sonner";
import { useCredentialVaultDocumentMutations } from "./use-credential-vault-document-mutations";
import {
  vaultSubjectUserId,
  vaultRecordQueryKey,
  vaultCandidatesQueryOptions,
  type CredentialVaultScope,
} from "../lib/credential-vault-scope";
import { useTRPC } from "@/trpc";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { cn } from "@/lib/utils";
import type { CredentialsView } from "@/features/credentials/components/credentials-view";
import { CredentialVaultEditModal } from "@/features/credentials/components/credential-vault-edit-modal/credential-vault-edit-modal";
import { CredentialDocumentPreviewModal } from "@/features/credentials/components/credential-card/credential-document-preview-modal";
import { RegistryVerificationDialog } from "../components/registry-report";
import { ProviderCredentialUploadPanel } from "../components/provider-credential-upload-panel";
import { CredentialVaultUpload } from "../components/credential-vault-upload";
import { PendingUploadsNotice } from "../components/pending-uploads-notice";
import { ProviderSpecialtiesSummary } from "../components/provider-specialties-summary";
import { CredentialSpecialtiesEditModal } from "../components/credential-specialties-edit-modal";
import { ProviderIdentitySection } from "../components/provider-identity-section";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCredentialDocumentPreview } from "./use-credential-document-url";
import { useCredentialVerificationPreview } from "./use-credential-verification-preview";
import { useCredentialVaultFiles } from "./use-credential-vault-files";
import type {
  Credential,
  CredentialDocument,
  CredentialsResult,
} from "../types";

export type CredentialVaultEditing = {
  isDialogOpen: boolean;
  cardActions: Pick<
    ComponentProps<typeof CredentialsView>,
    | "readOnly"
    | "onEditCredential"
    | "onOpenDocument"
    | "onOpenVerificationEvidence"
    | "isOpeningDocument"
    | "onRemoveDocument"
  >;
  modals: ReactNode;
  notice: ReactNode;
  filesView: ReactNode;
  uploadArea: ReactNode;
  summaryActions: ReactNode;
  sectionContent: Partial<Record<string, ReactNode>>;
  sectionBodies: Partial<Record<string, ReactNode>>;
  editCredential: (credential: Credential) => void;
  close: () => void;
};

const PENDING_UPLOAD_POLL_MS = 2500;

export function useCredentialVaultEditing({
  scope,
  canEdit = true,
  restrictionNotice,
  data,
  enabled = true,
  uploadButtonClassName,
  uploadPresentation = "toggle",
  onEditPersonalDetails,
  personalDetailsContent,
}: {
  scope: CredentialVaultScope;
  canEdit?: boolean;
  restrictionNotice?: ReactNode;
  data: CredentialsResult | null;
  enabled?: boolean;
  uploadButtonClassName?: string;
  uploadPresentation?: "toggle" | "dropzone";
  onEditPersonalDetails?: () => void;
  personalDetailsContent?: ReactNode;
}): CredentialVaultEditing {
  const userId = vaultSubjectUserId(scope);
  const isEditable = canEdit && !!data;
  const isHistorical =
    scope.kind === "organization" && !!scope.credentialViewId;
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Credential | null>(null);
  const [reviewDocumentId, setReviewDocumentId] = useState<string | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isEditingSpecialties, setIsEditingSpecialties] = useState(false);
  const uploaderId = useId();
  const UploadIcon = isUploadOpen ? X : FileUp;
  const usesDropzone = uploadPresentation === "dropzone";
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    pendingFileCount: 0,
  });
  const [isConfirmingUploadDeletion, setIsConfirmingUploadDeletion] =
    useState(false);
  const [documentToRemove, setDocumentToRemove] = useState<{
    credential: Credential;
    document: CredentialDocument;
  } | null>(null);
  const preview = useCredentialDocumentPreview({
    providerUserId: userId,
    expectedAffiliationId:
      scope.kind === "organization" ? scope.expectedAffiliationId : undefined,
    organizationBusinessUnitId:
      scope.kind === "organization"
        ? scope.organizationBusinessUnitId
        : undefined,
    credentialViewId:
      scope.kind === "organization" ? scope.credentialViewId : undefined,
  });
  const verificationPreview = useCredentialVerificationPreview(scope);
  const reviewItems = useQuery({
    ...vaultCandidatesQueryOptions(trpc, scope),
    enabled: enabled && Boolean(data) && !isHistorical,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((item) => item.state === "reading")
        ? PENDING_UPLOAD_POLL_MS
        : false,
  });
  const files = useCredentialVaultFiles({
    scope,
    canEdit: isEditable,
    data,
    reviewItems: isHistorical ? [] : reviewItems.data,
    refreshReviewItems: reviewItems.refetch,
    preview,
    uploadDisabled: uploadState.isUploading || uploadState.pendingFileCount > 0,
    onReviewCredential: reviewCredential,
  });
  const documents = useCredentialVaultDocumentMutations(scope);

  useEffect(() => {
    if (isEditable) {
      return;
    }
    setEditing(null);
    setReviewDocumentId(null);
    setIsEditingSpecialties(false);
    setDocumentToRemove(null);
    setIsConfirmingUploadDeletion(false);
    setUploadState({ isUploading: false, pendingFileCount: 0 });
  }, [isEditable]);

  async function deleteUpload(documentId: string) {
    if (!isEditable) {
      return;
    }
    try {
      await documents.deleteUpload(documentId);
      await queryClient.invalidateQueries({
        queryKey: trpc.credentials.pathKey(),
      });
      toast.success("File deleted");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not delete this file.",
      );
    }
  }

  async function removeDocument() {
    if (!isEditable || !documentToRemove?.credential.credentialId) {
      return;
    }
    try {
      await documents.removeDocument({
        credentialId: documentToRemove.credential.credentialId,
        documentId: documentToRemove.document.id,
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: vaultRecordQueryKey(trpc, scope),
        }),
        files.refresh(),
      ]);
      setDocumentToRemove(null);
      toast.success("Document removed from this credential");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not remove this document.",
      );
    }
  }

  const documentPreview = preview.document ? (
    <CredentialDocumentPreviewModal
      document={preview.document}
      url={preview.url}
      isLoading={preview.isLoading}
      isDownloading={files.isPreviewDownloading}
      onDownload={files.downloadPreview}
      onReview={editing ? undefined : files.reviewPreview}
      onClose={files.closePreview}
    />
  ) : null;
  let verificationEvidencePreview: ReactNode = null;
  if (verificationPreview.registryVerification) {
    verificationEvidencePreview = (
      <RegistryVerificationDialog
        verification={verificationPreview.registryVerification}
        isDownloading={verificationPreview.isDownloading}
        onDownload={verificationPreview.download}
        onClose={verificationPreview.close}
      />
    );
  } else if (verificationPreview.document) {
    verificationEvidencePreview = (
      <CredentialDocumentPreviewModal
        document={verificationPreview.document}
        description="Verification evidence for this credential."
        url={verificationPreview.url}
        error={verificationPreview.error}
        isLoading={verificationPreview.isLoading}
        isDownloading={verificationPreview.isDownloading}
        onDownload={verificationPreview.download}
        onClose={verificationPreview.close}
      />
    );
  }

  function closeEditor() {
    setEditing(null);
    setReviewDocumentId(null);
    preview.close();
    verificationPreview.close();
  }

  function editCredential(credential: Credential) {
    if (!isEditable) {
      return;
    }
    setReviewDocumentId(null);
    setEditing(credential);
  }

  function reviewCredential(credential: Credential, documentId: string) {
    if (!isEditable) {
      return;
    }
    setReviewDocumentId(documentId);
    setEditing(credential);
  }

  // Keep the modal mounted by anchor while passing fresh documents after a
  // review is accepted. Its form state owns the draft independently of this row.
  let editingCredential = editing;
  if (editing) {
    const current = data?.credentials.find((credential) => {
      if (editing.credentialId) {
        return credential.credentialId === editing.credentialId;
      }
      return credential.id === editing.id;
    });
    editingCredential = current ?? editing;
  }
  const hasVisibleNotice =
    (!usesDropzone &&
      (isUploadOpen ||
        uploadState.isUploading ||
        uploadState.pendingFileCount > 0)) ||
    reviewItems.isError ||
    (reviewItems.data?.length ?? 0) > 0;
  const notice = data ? (
    <>
      {restrictionNotice}
      {isEditable && (
        <div hidden={!hasVisibleNotice} className="space-y-4">
          {!usesDropzone && (
            <ProviderCredentialUploadPanel
              scope={scope}
              uploaderId={uploaderId}
              isOpen={isUploadOpen}
              onUploadStateChange={setUploadState}
            />
          )}
          {!usesDropzone &&
            (uploadState.isUploading || uploadState.pendingFileCount > 0) && (
              <p
                role="status"
                className="text-xs leading-relaxed text-slate-500"
              >
                Finish uploading or remove the remaining files before closing
                this panel.
              </p>
            )}
          {reviewItems.isError && (
            <div role="alert" className={PROFILE_FORM_STYLES.error}>
              <p>Could not load your uploaded documents.</p>
              <Button
                variant="link"
                className="h-auto px-0 pt-2 text-primary"
                onClick={() => void reviewItems.refetch()}
              >
                Try again
              </Button>
            </div>
          )}
          <PendingUploadsNotice
            appearance="profile"
            items={reviewItems.data ?? []}
            credentials={data.credentials}
            categories={data.categories}
            onReview={reviewCredential}
            onPreview={preview.openReview}
            onDelete={(documentId) => void deleteUpload(documentId)}
            deletingDocumentId={documents.deletingDocumentId}
            onDeleteDialogOpenChange={setIsConfirmingUploadDeletion}
          />
        </div>
      )}
    </>
  ) : null;

  const summaryActions =
    isEditable && !usesDropzone ? (
      <Button
        type="button"
        className={cn("gap-2 text-sm font-semibold", uploadButtonClassName)}
        aria-expanded={isUploadOpen}
        aria-controls={uploaderId}
        disabled={files.isBusy}
        onClick={() => setIsUploadOpen((open) => !open)}
      >
        <UploadIcon className="size-4" aria-hidden="true" />
        {isUploadOpen ? "Close uploader" : "Upload documents"}
      </Button>
    ) : null;

  const modals = (
    <>
      {isEditable && isEditingSpecialties && data && (
        <CredentialSpecialtiesEditModal
          scope={scope}
          credentials={data.credentials}
          onClose={() => setIsEditingSpecialties(false)}
        />
      )}
      {isEditable && editingCredential && (
        <CredentialVaultEditModal
          key={editingCredential.credentialId ?? editingCredential.id}
          credential={editingCredential}
          relatedCredentials={data?.credentials}
          scope={scope}
          onClose={closeEditor}
          onSaved={files.onCredentialSaved}
          onOpenDocument={preview.open}
          onOpenVerificationEvidence={verificationPreview.open}
          reviewDocumentId={reviewDocumentId}
          onOpenReviewDocument={preview.openReview}
        >
          {documentPreview}
          {verificationEvidencePreview}
        </CredentialVaultEditModal>
      )}
      {!editing && documentPreview}
      {!editing && verificationEvidencePreview}
      <AlertDialog
        open={isEditable && documentToRemove !== null}
        onOpenChange={(open) => {
          if (!open && !documents.isRemoving) {
            setDocumentToRemove(null);
          }
        }}
      >
        <AlertDialogContent className={PROFILE_FORM_STYLES.confirmation}>
          <AlertDialogHeader className="text-left">
            <AlertDialogTitle className={PROFILE_FORM_STYLES.title}>
              Remove this document?
            </AlertDialogTitle>
            <AlertDialogDescription className={PROFILE_FORM_STYLES.description}>
              {documentToRemove?.document.title ||
                documentToRemove?.document.fileName}{" "}
              will no longer appear on this credential. Its attachment history
              will be kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-2 gap-3 sm:space-x-0">
            <AlertDialogCancel
              className={cn(PROFILE_FORM_STYLES.secondaryButton, "mt-0")}
              disabled={documents.isRemoving}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={PROFILE_FORM_STYLES.dangerButton}
              disabled={documents.isRemoving}
              onClick={(event) => {
                event.preventDefault();
                void removeDocument();
              }}
            >
              {documents.isRemoving ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return {
    isDialogOpen:
      isEditingSpecialties ||
      editing !== null ||
      preview.document !== null ||
      verificationPreview.document !== null ||
      verificationPreview.registryVerification !== null ||
      documentToRemove !== null ||
      isConfirmingUploadDeletion ||
      documents.isDeleting ||
      uploadState.isUploading ||
      uploadState.pendingFileCount > 0 ||
      files.isBusy,
    cardActions: {
      readOnly: !isEditable,
      onEditCredential: isEditable ? editCredential : undefined,
      onOpenDocument: preview.open,
      onOpenVerificationEvidence: verificationPreview.open,
      isOpeningDocument: preview.isLoading,
      onRemoveDocument: isEditable ? setDocumentToRemove : undefined,
    },
    modals,
    notice,
    filesView: files.view,
    uploadArea:
      isEditable && usesDropzone ? (
        <div className="space-y-2">
          <CredentialVaultUpload
            scope={scope}
            disabled={files.isBusy}
            onUploadStateChange={setUploadState}
          />
          <p className="px-1 text-xs leading-relaxed text-slate-600">
            Update details or add supporting evidence. Review before saving.
          </p>
        </div>
      ) : null,
    summaryActions,
    sectionContent: {
      [CredentialCategory.BoardCertifications]: data ? (
        <ProviderSpecialtiesSummary
          credentials={data.credentials}
          onEdit={isEditable ? () => setIsEditingSpecialties(true) : undefined}
        />
      ) : null,
    },
    sectionBodies: {
      [CredentialCategory.Identity]: data ? (
        <ProviderIdentitySection
          onEditPersonalDetails={onEditPersonalDetails}
          personalDetailsContent={personalDetailsContent}
          appearance="panel"
          readOnly={!isEditable}
          credentials={data.credentials}
          userId={userId}
          onEditCredential={editCredential}
          onOpenDocument={preview.open}
          onOpenVerificationEvidence={verificationPreview.open}
          isOpeningDocument={preview.isLoading}
          onRemoveDocument={isEditable ? setDocumentToRemove : undefined}
        />
      ) : undefined,
    },
    editCredential,
    close: () => {
      closeEditor();
      setIsUploadOpen(false);
      files.close();
      setIsEditingSpecialties(false);
      if (!documents.isRemoving) {
        setDocumentToRemove(null);
      }
    },
  };
}
